import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import StudentShell from "../components/StudentShell/StudentShell";
import * as lessonsService from "../services/lessons";
import { localizeField } from "../utils/localize";
import "./Lessons.css";

const STATUS_LABELS_KEY = {
  scheduled: "lessons.statusScheduled",
  completed: "lessons.statusCompleted",
  cancelled: "lessons.statusCancelled",
};

function formatDateTime(value, lang = "ar") {
  if (!value) return "-";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const locale = lang === "ar" ? "ar-TN" : lang;
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "full",
      timeStyle: "short",
    }).format(date);
  } catch (error) {
    return value;
  }
}

export default function LessonDetail() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { accessToken, user, effectiveRole } = useAuth();
  const { t, language } = useTranslation();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meetingLoading, setMeetingLoading] = useState(false);
  const [meetingError, setMeetingError] = useState(null);

  const wrapStudent = (node) =>
    effectiveRole === "student" ? <StudentShell>{node}</StudentShell> : node;

  const canManage = Boolean(user && ["admin", "teacher"].includes(user.role));

  useEffect(() => {
    if (
      ["admin", "superadmin", "support", "accounting"].includes(effectiveRole)
    ) {
      navigate("/admin/timetable", { replace: true });
    }
  }, [effectiveRole, navigate]);

  useEffect(() => {
    let ignore = false;

    async function loadLesson() {
      if (!lessonId || !accessToken) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const payload = await lessonsService.fetchLesson({
          accessToken,
          lessonId,
        });
        if (!ignore) {
          setLesson(payload);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError.message);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadLesson();
    return () => {
      ignore = true;
    };
  }, [lessonId, accessToken]);

  const statusClassName = useMemo(() => {
    if (!lesson) {
      return "status";
    }
    return `status status--${lesson.status}`;
  }, [lesson]);

  const handleDelete = async () => {
    if (!lesson || !canManage) {
      return;
    }
    const confirmed = window.confirm(t("lessonDetail.deleteConfirm"));
    if (!confirmed) {
      return;
    }

    try {
      await lessonsService.deleteLesson(accessToken, lesson.id);
      navigate("/lessons");
    } catch (deleteError) {
      setError(deleteError.message);
    }
  };

  const handleCreateMeeting = async () => {
    if (!lesson || !canManage || meetingLoading) {
      return;
    }
    setMeetingLoading(true);
    setMeetingError(null);
    try {
      const payload = await lessonsService.createLessonMeeting({
        accessToken,
        lessonId: lesson.id,
      });
      setLesson(payload);
    } catch (meetingErr) {
      setMeetingError(meetingErr.message);
    } finally {
      setMeetingLoading(false);
    }
  };

  if (loading) {
    return wrapStudent(
      <div className="lessons-page">
        <div className="lessons-shell">
          <p className="lessons-muted">{t("lessonDetail.loading")}</p>
        </div>
      </div>,
    );
  }

  if (error) {
    return wrapStudent(
      <div className="lessons-page">
        <div className="lessons-shell">
          <div className="lessons-error" role="alert">
            <p>
              {t("lessonDetail.error")}
              {error}
            </p>
            <p>
              <Link
                to="/lessons"
                className="lessons-action lessons-action--ghost"
              >
                {t("lessonDetail.backToList")}
              </Link>
            </p>
          </div>
        </div>
      </div>,
    );
  }

  if (!lesson) {
    return wrapStudent(
      <div className="lessons-page">
        <div className="lessons-shell">
          <p className="lessons-muted">{t("lessonDetail.notFound")}</p>
        </div>
      </div>,
    );
  }

  return wrapStudent(
    <div className="lessons-page">
      <div className="lessons-shell lesson-detail">
        <header className="lesson-detail__head">
          <div>
            <p className="lessons-muted">
              {t("lessonDetail.idLabel")}: {lesson.id}
            </p>
            <h1>{localizeField(lesson, "title", language)}</h1>
            <div className="lesson-detail__meta">
              <span className={statusClassName}>
                {t(STATUS_LABELS_KEY[lesson.status]) || lesson.status}
              </span>
              <span>
                {t("lessonDetail.scheduledAt")}:{" "}
                {formatDateTime(lesson.scheduled_at, language)}
              </span>
            </div>
          </div>
          <div className="lesson-detail__actions">
            <Link
              to="/lessons"
              className="lessons-action lessons-action--ghost"
            >
              {t("lessonDetail.backToList")}
            </Link>
            {lesson.meeting_url ? (
              <a
                href={lesson.meeting_url}
                className="lessons-action lessons-action--primary"
                target="_blank"
                rel="noreferrer"
              >
                {t("lessonDetail.joinMeeting")}
              </a>
            ) : canManage ? (
              <button
                type="button"
                className="lessons-action lessons-action--primary"
                onClick={handleCreateMeeting}
                disabled={meetingLoading}
              >
                {meetingLoading
                  ? t("lessonDetail.generatingMeeting")
                  : t("lessonDetail.generateMeeting")}
              </button>
            ) : null}
            {canManage && (
              <Link
                to={`/lessons/${lesson.id}/edit`}
                className="lessons-action lessons-action--ghost"
              >
                {t("lessonDetail.edit")}
              </Link>
            )}
            {canManage && (
              <button
                type="button"
                className="lessons-action lessons-action--danger"
                onClick={handleDelete}
              >
                {t("lessonDetail.delete")}
              </button>
            )}
          </div>
        </header>

        {meetingError ? (
          <div className="lessons-error" role="alert">
            <p>{meetingError}</p>
          </div>
        ) : null}

        {lesson.description && (
          <section>
            <h2>{t("lessonDetail.description")}</h2>
            <p>{localizeField(lesson, "description", language)}</p>
          </section>
        )}

        <section className="lesson-detail__grid">
          <article className="lesson-detail__card">
            <p className="lesson-detail__label">{t("lessonDetail.student")}</p>
            <p className="lesson-detail__value">
              {localizeField(lesson.student, "full_name", language)}
            </p>
            <p className="lessons-muted">
              {t("common.id")}: {lesson.student.id}
            </p>
          </article>
          <article className="lesson-detail__card">
            <p className="lesson-detail__label">{t("lessonDetail.teacher")}</p>
            <p className="lesson-detail__value">
              {localizeField(lesson.teacher, "full_name", language) ||
                lesson.teacher.email}
            </p>
            <p className="lessons-muted">
              {t("common.id")}: {lesson.teacher.id}
            </p>
          </article>
          <article className="lesson-detail__card">
            <p className="lesson-detail__label">{t("lessonDetail.duration")}</p>
            <p className="lesson-detail__value">
              {lesson.duration_minutes} {t("lessonDetail.minutes")}
            </p>
            {lesson.pack && (
              <p className="lessons-muted">
                {t("lessonDetail.partOfPack")}{" "}
                {localizeField(lesson.pack, "name", language)}
              </p>
            )}
          </article>
          <article className="lesson-detail__card">
            <p className="lesson-detail__label">
              {t("lessonDetail.createdAt")}
            </p>
            <p className="lesson-detail__value">
              {formatDateTime(lesson.created_at, language)}
            </p>
            <p className="lessons-muted">
              {t("lessonDetail.updatedAt")}:{" "}
              {formatDateTime(lesson.updated_at, language)}
            </p>
          </article>
        </section>

        {lesson.pack && (
          <section className="lesson-detail__card">
            <h2>{t("lessonDetail.packInfo")}</h2>
            <p className="lesson-detail__label">{t("lessonDetail.pack")}</p>
            <p className="lesson-detail__value">
              {localizeField(lesson.pack, "name", language)}
            </p>
            <p className="lessons-muted">
              {t("lessonDetail.subject")}:{" "}
              {localizeField(lesson.pack.subject, "name", language)} •{" "}
              {t("lessonDetail.price")}: {lesson.pack.price?.amount} TND
            </p>
            {lesson.pack.summary && (
              <p>{localizeField(lesson.pack, "summary", language)}</p>
            )}
          </section>
        )}
      </div>
    </div>,
  );
}
